import React, { useState } from 'react';
import { Info } from 'lucide-react';
import type { Question, FormResponses } from '../../../../types/publicForm.types';
import { useFormContext } from '../../../../public_home/components/forms/FormContext';
import { V1TextQuestion } from './questions/V1TextQuestion';
import { V1TextareaQuestion } from './questions/V1TextareaQuestion';
import { SelectQuestion } from '../../../../public_home/components/forms/questions/SelectQuestion';
import { RadioQuestion } from '../../../../public_home/components/forms/questions/RadioQuestion';
import { CheckboxQuestion } from '../../../../public_home/components/forms/questions/CheckboxQuestion';
import { FileUploadQuestion } from '../../../../public_home/components/forms/questions/FileUploadQuestion';
import { V1ConditionalQuestions } from './V1ConditionalQuestions';
import { V1QuestionHeader } from './questions/V1QuestionHelpers';

interface V1FormQuestionProps {
  question: Question;
  primaryColor: string;
  isConditional?: boolean;
}

/**
 * V1 Form Question Component - FROZEN
 *
 * Special handling for V1 forms:
 * - When showComments is true and there are NO answers, the comment field IS the question
 * - When showComments is true and there ARE answers, show both the answers AND a comment field
 */
export const V1FormQuestion: React.FC<V1FormQuestionProps> = ({ question, primaryColor, isConditional = false }) => {
  const { state, actions } = useFormContext();
  const [expandedMoreInfo, setExpandedMoreInfo] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Map answer styles to normalized types
  const getNormalizedStyle = (style: string): string => {
    const mappings: Record<string, string> = {
      'dropdown': 'select',
      'multiple_choice': 'radio',
      'checkboxes': 'checkbox',
      'long_text': 'textarea',
      'short_text': 'text',
      'paragraph': 'textarea',
      'multiselect': 'multiselect',
      'file': 'file',
    };
    return mappings[style] || style;
  };

  const normalizedStyle = getNormalizedStyle(question.answerStyle);

  // Get the value, ensuring it's the correct type for the question
  const rawValue = state.formData[question.id];

  // Ensure checkbox and multiselect questions always have array values
  const value = (normalizedStyle === 'checkbox' || normalizedStyle === 'multiselect')
    ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
    : (rawValue || '');

  const error = state.validationErrors[question.id];

  // Debug logging for validation errors
  if (error) {
    console.log(`Question ${question.id} (${question.name}) has error:`, error);
  }

  // Check if this question has a header/title to display above it
  const hasHeader = question.options?.header && question.options.header.trim() !== '';

  // V1 Logic: If showComments is true and no answers, the comment field IS the main field
  const isCommentOnlyQuestion = question.showComments &&
    (!question.showAnswerOptions || !question.answers || question.answers.filter(a => !a._destroy).length === 0);

  // Handle input change
  const handleInputChange = (newValue: any) => {
    actions.updateField(question.id, newValue);

    // Check if we need to update visible child questions
    if (question.answers && question.answers.length > 0) {
      const visibleChildIds = new Set(state.visibleChildQuestions);

      // Remove all child questions for this parent first
      question.answers.forEach(answer => {
        if (answer.childQuestions) {
          answer.childQuestions.forEach(child => {
            visibleChildIds.delete(String(child.id));
            // Clear form data for hidden child questions
            if (state.formData[child.id]) {
              actions.updateField(child.id, '');
            }
          });
        }
      });

      // Add child questions for selected answer(s)
      if (normalizedStyle === 'checkbox' || normalizedStyle === 'multiselect') {
        // Handle multiple selections
        const selectedIds = Array.isArray(newValue) ? newValue : [];
        selectedIds.forEach(answerId => {
          const answer = question.answers.find(a => String(a.id) === String(answerId));
          if (answer?.childQuestions) {
            answer.childQuestions.forEach(child => {
              visibleChildIds.add(String(child.id));
            });
          }
        });
      } else {
        // Handle single selection
        const answer = question.answers.find(a => String(a.id) === String(newValue));
        if (answer?.childQuestions) {
          answer.childQuestions.forEach(child => {
            visibleChildIds.add(String(child.id));
          });
        }
      }

      actions.updateVisibleChildQuestions(visibleChildIds);
    }
  };

  // For comment-only questions, use the main question ID for the value
  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isCommentOnlyQuestion) {
      actions.updateField(question.id, e.target.value);
    } else {
      // For questions with answers AND comments, use separate comment field
      actions.updateField(`${question.id}_comment`, e.target.value);
    }
  };

  const renderQuestion = () => {
    // V1 Special Case: Comment-only question
    if (isCommentOnlyQuestion) {
      const isLargeComments = question.options?.large_comments || question.options?.largeComments;

      const commonProps = {
        question: question,
        value: value,
        onChange: handleInputChange,
        onBlur: () => actions.touchField(question.id),
        error: error,
        primaryColor,
      };

      // Use textarea for large comments, text input for small
      if (isLargeComments) {
        return <V1TextareaQuestion {...commonProps} />;
      } else {
        return <V1TextQuestion {...commonProps} type="text" />;
      }
    }

    // Normal question rendering for non-comment-only questions
    const questionWithHelpers = {
      ...question,
      description: question.helpText,
    };

    const commonProps = {
      question: questionWithHelpers,
      value,
      onChange: handleInputChange,
      onBlur: () => actions.touchField(question.id),
      error: error,
      primaryColor,
      moreInfo: question.moreInfo,
      isMoreInfoExpanded: expandedMoreInfo,
      onToggleMoreInfo: question.moreInfo ? () => setExpandedMoreInfo(!expandedMoreInfo) : undefined,
    };

    switch (normalizedStyle) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'date':
        return <V1TextQuestion {...commonProps} type={normalizedStyle} />;

      case 'textarea':
        return <V1TextareaQuestion {...commonProps} />;

      case 'select':
        const isMultiSelect = question.allowMultipleAnswers === true;
        return <SelectQuestion {...commonProps} isMulti={isMultiSelect} onDropdownToggle={setIsDropdownOpen} />;

      case 'multiselect':
        return <SelectQuestion {...commonProps} isMulti={true} onDropdownToggle={setIsDropdownOpen} />;

      case 'radio':
        return <RadioQuestion {...commonProps} />;

      case 'checkbox':
        return <CheckboxQuestion {...commonProps} />;

      case 'file':
        return <FileUploadQuestion {...commonProps} />;

      default:
        return (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  Unsupported Question Type
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  The answer style "{question.answerStyle}" is not yet supported.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderCommentField = () => {
    const commentId = `${question.id}_comment`;
    const commentValue = state.formData[commentId] || '';
    const isLargeComments = question.options?.large_comments || question.options?.largeComments;

    return (
      <div className="mt-4">
        <label
          htmlFor={commentId}
          className="block text-sm font-medium text-gray-600 mb-2"
        >
          Additional Comments (Optional)
        </label>
        {isLargeComments ? (
          <textarea
            id={commentId}
            placeholder="Please provide any additional comments..."
            value={commentValue}
            onChange={handleCommentChange}
            rows={3}
            className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
          />
        ) : (
          <input
            type="text"
            id={commentId}
            placeholder="Please provide any additional comments..."
            value={commentValue}
            onChange={handleCommentChange}
            className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
          />
        )}
      </div>
    );
  };

  // Helper to check if question has conditional questions
  const hasConditionalQuestions = (q: Question): boolean => {
    if (!q.answers) return false;
    return q.answers.some(answer =>
      answer.childQuestions &&
      answer.childQuestions.length > 0 &&
      answer.childQuestions.some(child => !child._destroy)
    );
  };

  return (
    <div id={`question-${question.id}`} className={`space-y-4 mt-4 mb-6 relative ${isDropdownOpen ? 'z-[1000]' : ''}`}>
      {/* Optional header/title above the question */}
      {hasHeader && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {question.options.header}
          </h3>
          {question.helpText && (
            <p className="mt-1 text-sm text-gray-600">{question.helpText}</p>
          )}
        </div>
      )}

      {/* Question Label */}
      <V1QuestionHeader question={question} className="font-medium" />

      {/* Question Input */}
      <div className="min-h-[50px] relative">
        {renderQuestion()}
      </div>

      {/* Additional Comment Field (only for questions with answers AND showComments) */}
      {question.showComments && !isCommentOnlyQuestion && renderCommentField()}

      {/* Conditional Questions */}
      {hasConditionalQuestions(question) && (
        <V1ConditionalQuestions
          parentQuestion={question}
          selectedValue={value}
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
};